'use client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, use, useState, type ReactNode } from 'react';
import type { MasarClient } from '../client';
import { isApiError } from '../errors';

const ClientCtx = createContext<MasarClient | null>(null);

/** Retries network and 5xx failures twice; never retries 4xx (they will fail the same way). */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error)) {
    if (error.status >= 400 && error.status < 500 && error.status !== 408) return false;
  }
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}

export function ApiProvider(props: { client: MasarClient; queryClient?: QueryClient; children: ReactNode }) {
  const [qc] = useState(() => props.queryClient ?? createQueryClient());
  return (
    <ClientCtx value={props.client}>
      <QueryClientProvider client={qc}>{props.children}</QueryClientProvider>
    </ClientCtx>
  );
}

/** The typed API client from <ApiProvider>. */
export function useApi(): MasarClient {
  const c = use(ClientCtx);
  if (!c) throw new Error('useApi outside <ApiProvider>');
  return c;
}
